import { ReactElement } from 'react';

import { IconProps } from '@components/icons';
import { IconCopy } from '@components/icons/copy';
import { IconEdit } from '@components/icons/edit';
import { IconSend } from '@components/icons/send';
import { IconTrash } from '@components/icons/trash';
import { useClipboard } from '@hooks/use-clipboard';
import { useScopedTranslate } from '@locale/translate';

import {
  CellActionKey,
  getCopyableValue,
  getEmptyEditValue,
  getMenuActionKeys,
} from './cell-actions';
import { editableCellLocale } from './locale';

import { isEmptyColumnValue } from '../table-utils';
import { AvailableColumn, DataItemType } from '../types';

export type CellAction = {
  key: CellActionKey;
  label: string;
  icon: ReactElement<IconProps>;
  variant: 'base' | 'danger';
  onSelect(): void;
};

// The actions a filled cell offers before it opens its editor. An empty cell
// has nothing to copy, send or remove, so it goes straight into editing.
export const useCellActions = ({
  column,
  row,
  value,
  openEditor,
  close,
}: {
  column: AvailableColumn<any>;
  row: DataItemType;
  value: any;
  openEditor(): void;
  close(): void;
}): Array<CellAction> => {
  const { t } = useScopedTranslate(editableCellLocale);
  const { onCopy } = useClipboard();

  if (!column.onEdit || isEmptyColumnValue(value)) {
    return [];
  }

  const build: Record<CellActionKey, () => CellAction> = {
    copy: () => ({
      key: 'copy',
      label: t('copy'),
      icon: <IconCopy />,
      variant: 'base',
      onSelect: () => {
        onCopy(getCopyableValue(column.type, value) ?? '', t('copied'));
        close();
      },
    }),
    edit: () => ({
      key: 'edit',
      label: t('core.edit'),
      icon: <IconEdit />,
      variant: 'base',
      onSelect: openEditor,
    }),
    sendEmail: () => ({
      key: 'sendEmail',
      label: t('sendEmail'),
      icon: <IconSend />,
      variant: 'base',
      onSelect: () => {
        window.open(`mailto:${getCopyableValue(column.type, value) ?? ''}`);
        close();
      },
    }),
    remove: () => ({
      key: 'remove',
      label: t('core.remove'),
      icon: <IconTrash />,
      variant: 'danger',
      onSelect: () => {
        close();
        void column.onEdit?.(row, getEmptyEditValue(column.type, value));
      },
    }),
  };

  return getMenuActionKeys(column.type, value, { isClearable: column.isClearable }).map((key) =>
    build[key]()
  );
};
